/**
 * Logique pure du détail d'une tournée en vigueur (#891) : lignes du déroulé, heure prévue et
 * chauffeurs à contacter. Sans JSX ni React Query, testée par `operations-detail.test.mjs`.
 */

import { LOGISTICS_DAY_SECONDS, logisticsTimeToSeconds } from '@gozoki/api-types'
import type {
  ActiveTourDetail,
  ActiveTourPlannedTime,
  ActiveTourPoint,
  ActiveTourSegment,
} from '~/lib/logistics-operations-query'
import {
  type DetailMapMarker,
  firstPassageRanksOf,
  formatHoursMinutes,
  isDeliveredPlace,
  pathMapMarkersOf,
  placeKey,
} from './scenario-view'

/** Ligne du déroulé : un point de passage, le véhicule de son segment et son heure prévue. */
export interface TourScheduleRow {
  /** Unique dans le déroulé : un même lieu revient (hub à l'arrivée de l'approche, puis au départ). */
  key: string
  place: ActiveTourPoint['place']
  vehicle: ActiveTourSegment['vehicle']
  time: ActiveTourPlannedTime
}

/**
 * Déroulé, segment après segment : chaque point de passage, à son heure de départ pour l'origine
 * du segment, d'arrivée pour les autres. Un hub qui termine l'approche et ouvre le dernier km
 * apparaît deux fois, avec le camion qui y arrive puis celui qui en repart.
 */
export const tourScheduleRowsOf = (segments: readonly ActiveTourSegment[]): TourScheduleRow[] =>
  segments.flatMap((segment) =>
    segment.points.flatMap((point, index) => {
      const time = index === 0 ? point.departure : point.arrival
      return time === null
        ? []
        : [{ key: `${segment.id}-${index}`, place: point.place, vehicle: segment.vehicle, time }]
    }),
  )

/** Secondes depuis minuit du jour de départ d'un passage : le lendemain compte 24 h de plus. */
const plannedSecondsOf = ({ time, dayOffset }: ActiveTourPlannedTime): number =>
  dayOffset * LOGISTICS_DAY_SECONDS + logisticsTimeToSeconds(time)

/**
 * Rang de passage de chaque boutique (clé `placeKey`), dans l'ordre CHRONOLOGIQUE de sa première
 * arrivée prévue, tous camions confondus : la même règle que le détail d'une simulation
 * (`storeVisitOrderOf`, `firstPassageRanksOf`). À heure égale, l'ordre des segments puis des
 * arrêts.
 */
export const tourStoreVisitOrderOf = (
  segments: readonly ActiveTourSegment[],
): Map<string, number> =>
  firstPassageRanksOf(
    segments
      .flatMap((segment) => segment.points)
      .flatMap((point) =>
        point.arrival !== null && isDeliveredPlace(point.place)
          ? [{ key: placeKey(point.place), seconds: plannedSecondsOf(point.arrival) }]
          : [],
      )
      .sort((a, b) => a.seconds - b.seconds)
      .map(({ key }) => key),
  )

/**
 * Marqueurs de la carte du détail : les lieux des segments de la tournée (ceux du déroulé),
 * boutiques numérotées dans l'ordre de passage.
 */
export const tourMapMarkersOf = (segments: readonly ActiveTourSegment[]): DetailMapMarker[] =>
  pathMapMarkersOf(
    segments.map((segment) => segment.points.map((point) => point.place)),
    tourStoreVisitOrderOf(segments),
  )

/** Heure prévue d'un passage, comme l'onglet Horaires du simulateur : « 05h05 », « J+1 00h40 ». */
export const formatPlannedTime = (time: ActiveTourPlannedTime): string =>
  formatHoursMinutes(plannedSecondsOf(time))

export type TourDriverContact = ActiveTourSegment['driver']

/** Chauffeurs à joindre, un par profil, dans l'ordre des segments. */
export const tourDriverContactsOf = (
  detail: Pick<ActiveTourDetail, 'segments'>,
): TourDriverContact[] => {
  const drivers = new Map<number, TourDriverContact>()
  for (const segment of detail.segments) {
    if (!drivers.has(segment.driver.id)) drivers.set(segment.driver.id, segment.driver)
  }
  return [...drivers.values()]
}

/**
 * Nom de chaque lieu du déroulé, par `placeKey` : sujets des avertissements de la charge (#982).
 * Un lieu supprimé depuis la bascule (libellé `null`) n'a pas d'entrée.
 */
export const tourPlaceLabelsOf = (segments: readonly ActiveTourSegment[]): Map<string, string> =>
  new Map(
    segments.flatMap((segment) =>
      segment.points.flatMap((point) =>
        point.place.label === null ? [] : [[placeKey(point.place), point.place.label] as const],
      ),
    ),
  )
